import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navigation from '@/shared/components/Navigation';
import ToastContainer from '@/shared/components/ToastContainer';
import { useCrmMyAccess } from '@/hooks/queries';

const NAV_ITEMS = [
  { path: '/crm', label: 'Dashboard', icon: '📊', exact: true },
  { path: '/crm/contacts', label: 'Contacts', icon: '👥' },
  { path: '/crm/opportunities', label: 'Opportunities', icon: '💰' },
  { path: '/crm/settings', label: 'Settings', icon: '⚙️', adminOnly: true },
];

export default function CrmLayout({ children, toasts, onCloseToast }) {
  const location = useLocation();
  const { data: myAccess } = useCrmMyAccess();
  
  // Filter nav items based on admin status
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter(item => !item.adminOnly || myAccess?.isAdmin);
  }, [myAccess?.isAdmin]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      {toasts && <ToastContainer toasts={toasts} onCloseToast={onCloseToast} />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          {myAccess?.isAdmin && (
            <Link
              to="/admin-portal"
              className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center mb-4"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Admin Portal
            </Link>
          )}
          
          <h1 className="text-3xl font-bold text-gray-900">CRM</h1>
          {!myAccess?.isAdmin && myAccess?.allowedSources?.length > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              Viewing: {myAccess.allowedSources.join(', ')}
              {myAccess.contactsWithSource !== undefined && (
                <span className="ml-2 text-gray-400">
                  ({myAccess.contactsWithSource} contacts)
                </span>
              )}
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
            {visibleNavItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap
                    ${isActive
                      ? 'border-zapt-purple text-zapt-purple'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Page Content */}
        <div>
          {children}
        </div>
      </div>
    </div>
  );
}
